import { useMemo, useState } from "react"
import { Link, router } from "@inertiajs/react"
import {
  Sun,
  Moon,
  Monitor,
  SlidersHorizontal,
  Bell,
  User,
  Eye,
} from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { Switch } from "@/components/ui/switch"
import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"
import { Input } from "@/components/ui/input"
import { getTheme, setTheme } from "@/lib/theme"
import { cn } from "@/lib/utils"

// ---- building blocks ------------------------------------------------------

function Card({ Icon, title, description, children }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center gap-[11px] border-b border-border px-[22px] py-4">
        <div className="grid size-[34px] shrink-0 place-items-center rounded-[9px] border border-border bg-secondary">
          <Icon className="size-[17px] text-muted-foreground" strokeWidth={1.85} />
        </div>
        <div>
          <h3 className="font-display text-[14.5px] font-bold">{title}</h3>
          {description && <p className="mt-px text-xs text-ink-3">{description}</p>}
        </div>
      </div>
      {children}
    </div>
  )
}

// MOBILE-PLAN :69 — at ≤640 the row wraps and its control caps at 100% width
// (prototype `.set-row{flex-wrap:wrap;gap:12px 24px}` +
// `.set-row .ctl{flex-shrink:1;min-width:0;max-width:100%}`). Tailwind v4
// compiles `max-[N]` EXCLUSIVE, so "≤640" is written `max-[641px]`.
//
// The card clips (`overflow-hidden`), so before this the controls did not
// widen `main` — they were cut off inside the card instead, which is worse:
// at 365 the "Never" cadence button sat at right=444 against a card content
// edge of 335, i.e. unreachable with no scrollbar to reveal it.
function Row({ title, hint, children }) {
  return (
    <div
      className="flex items-center justify-between gap-6 border-b border-border px-[22px] py-4 last:border-b-0 max-[641px]:flex-wrap max-[641px]:gap-y-3"
      data-testid="settings-row"
    >
      <div className="min-w-0" data-testid="settings-title">
        <h4 className="text-[13.5px] font-semibold">{title}</h4>
        {hint && <p className="mt-0.5 max-w-[440px] text-xs leading-snug text-ink-3">{hint}</p>}
      </div>
      <div className="shrink-0 max-[641px]:w-full max-[641px]:min-w-0" data-testid="settings-control">
        {children}
      </div>
    </div>
  )
}

const THEME_OPTIONS = [
  { key: "light", label: "Light", Icon: Sun },
  { key: "dark", label: "Dark", Icon: Moon },
  { key: "system", label: "System", Icon: Monitor },
]

// The canonical, app-wide theme control. Persists immediately via lib/theme
// (the `.dark` class + localStorage) — it's not part of the Save bar.
function ThemePicker() {
  const [mode, setMode] = useState(() => getTheme())

  function choose(key) {
    setTheme(key)
    setMode(key)
  }

  const note =
    mode === "system"
      ? "Following your device."
      : mode === "dark"
        ? "Always use the dark theme."
        : "Always use the light theme."

  return (
    <div className="px-[22px] py-5">
      {/* No MOBILE-PLAN row governs the theme picker, but three `flex-1` tiles
          measured 36px past the card's clipping edge at 365 ("System" at
          right=371 vs a content edge of 335 — the tile was cut, not scrollable).
          `flex-1` cannot shrink past its label, so the three go one-per-row in
          the phone band, the same 1-col fold the bundle gives every other 3-up
          strip at ≤560. 561 upward keeps three across (measured 144px each). */}
      <div className="flex gap-3 max-[561px]:flex-col" data-testid="theme-picker">
        {THEME_OPTIONS.map(({ key, label, Icon }) => {
          const on = mode === key
          return (
            <button
              key={key}
              type="button"
              onClick={() => choose(key)}
              aria-pressed={on}
              className={cn(
                "flex flex-1 flex-col items-center gap-[9px] rounded-[13px] border-[1.5px] p-4 transition-colors",
                on
                  ? "border-primary bg-[var(--red-soft)]"
                  : "border-border bg-secondary hover:border-line-2"
              )}
            >
              <span
                className={cn(
                  "flex h-14 w-full overflow-hidden rounded-[9px] border border-line-2",
                  key === "light" && "bg-[#f4f4f2]",
                  key === "dark" && "bg-[#1f1f1f]",
                  key === "system" && "bg-[linear-gradient(135deg,#f4f4f2_50%,#1f1f1f_50%)]"
                )}
              >
                <span
                  className={cn(
                    "w-[30%]",
                    key === "light" && "bg-[#fafaf8]",
                    key === "dark" && "bg-[#181818]",
                    key === "system" && "bg-[linear-gradient(135deg,#fafaf8_50%,#181818_50%)]"
                  )}
                />
                <span className="flex flex-1 flex-col gap-[5px] p-2">
                  <span className="h-[7px] w-3/5 rounded bg-primary" />
                  <span
                    className={cn(
                      "h-[7px] rounded",
                      key === "light" ? "bg-[#e0e0da]" : key === "dark" ? "bg-[#303030]" : "bg-[#80807a]"
                    )}
                  />
                  <span
                    className={cn(
                      "h-[7px] w-4/5 rounded",
                      key === "light" ? "bg-[#e0e0da]" : key === "dark" ? "bg-[#303030]" : "bg-[#80807a]"
                    )}
                  />
                </span>
              </span>
              <span
                className={cn(
                  "flex items-center gap-1.5 text-[13px] font-semibold",
                  on ? "text-primary" : "text-muted-foreground"
                )}
              >
                <Icon className="size-4" strokeWidth={2} />
                {label}
              </span>
            </button>
          )
        })}
      </div>
      <p className="mt-3.5 text-xs text-ink-3">{note}</p>
    </div>
  )
}

const CADENCE = [
  { value: "email_always", label: "Always" },
  { value: "email_offline", label: "When offline" },
  { value: "email_never", label: "Never" },
]

function Segmented({ value, onChange, options }) {
  return (
    // MOBILE-PLAN :69 — the segmented control wraps at ≤640 (prototype `.seg2`).
    <div
      className="inline-flex gap-0.5 rounded-[10px] border border-border bg-secondary p-[3px] max-[641px]:flex-wrap"
      data-testid="segmented"
    >
      {options.map((o) => {
        const on = value === o.value
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              "rounded-lg px-3.5 py-[7px] text-[12.5px] font-semibold whitespace-nowrap transition-colors",
              on
                ? "bg-card text-foreground shadow-[0_1px_4px_rgba(0,0,0,0.08)]"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

// ---- page -----------------------------------------------------------------

export default function Settings({ settings }) {
  const { account, general, notifications } = settings

  const baseline = useMemo(
    () => ({
      usernameAsDefault: general.usernameAsDefault,
      openNewTab: general.openNewTab,
      emailCadence: notifications.emailCadence,
      playSound: notifications.playSound,
      remindersAndOffers: notifications.remindersAndOffers,
      username: account.username ?? "",
      timezone: account.timezone ?? "",
    }),
    [general, notifications, account]
  )

  const [form, setForm] = useState(baseline)
  const [saving, setSaving] = useState(false)

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }))
  const dirty = useMemo(
    () => Object.keys(baseline).some((k) => baseline[k] !== form[k]),
    [baseline, form]
  )

  // R1 requires the saved timezone stay visible. The default (User::DEFAULT_TIMEZONE
  // = "EST") is not one of the ActiveSupport::TimeZone.all names that back
  // account.timezones, so the Combobox would find no matching option and fall back
  // to its placeholder — hiding the value the customer actually has saved. Pin the
  // current value as an option (labelled with the server's timezoneLabel) whenever
  // it is not already present, so the current value is always resolvable.
  const timezoneOptions = useMemo(() => {
    const opts = account.timezones ?? []
    const current = form.timezone
    if (current && !opts.some((o) => o.value === current)) {
      return [{ value: current, label: account.timezoneLabel ?? current }, ...opts]
    }
    return opts
  }, [account.timezones, account.timezoneLabel, form.timezone])

  function save() {
    setSaving(true)
    router.patch(
      "/settings",
      {
        general: {
          usernameAsDefault: form.usernameAsDefault,
          openNewTab: form.openNewTab,
        },
        notifications: {
          emailCadence: form.emailCadence,
          playSound: form.playSound,
          remindersAndOffers: form.remindersAndOffers,
        },
        account: {
          username: form.username,
          timezone: form.timezone,
        },
      },
      {
        preserveScroll: true,
        onFinish: () => setSaving(false),
      }
    )
  }

  return (
    <>
      <div className="mx-auto flex max-w-[900px] flex-col gap-[22px] px-[30px] pt-[26px]">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-[-0.4px]">Settings</h1>
          <p className="mt-1 text-[13px] text-ink-3">
            Manage how Dibbly looks, what you're notified about, and your account
            details.
          </p>
        </div>

        <Card
          Icon={Sun}
          title="Appearance"
          description="Choose how Dibbly looks on this device."
        >
          <ThemePicker />
        </Card>

        <Card
          Icon={SlidersHorizontal}
          title="General"
          description="Defaults for how projects open and display."
        >
          <Row
            title="Use username as default name"
            hint="Show your username instead of your full name to freelancers on your projects."
          >
            <Switch
              checked={form.usernameAsDefault}
              onCheckedChange={set("usernameAsDefault")}
            />
          </Row>
          <Row
            title="Open orders in a new tab"
            hint="Opening a project from the list launches it in a separate browser tab."
          >
            <Switch checked={form.openNewTab} onCheckedChange={set("openNewTab")} />
          </Row>
        </Card>

        <Card
          Icon={Bell}
          title="Notifications"
          description="How and when we reach you about project activity."
        >
          <Row
            title="Email notifications"
            hint="Get emailed about messages, approvals, and delivery updates."
          >
            <Segmented
              value={form.emailCadence}
              onChange={set("emailCadence")}
              options={CADENCE}
            />
          </Row>
          {/* Approved customer copy: do not edit the title or hint here. */}
          <Row
            title="Reminders and offers"
            hint="Turn this off to stop reminder and offer emails while still getting emails about your projects."
          >
            <Switch
              checked={form.remindersAndOffers}
              onCheckedChange={set("remindersAndOffers")}
            />
          </Row>
          <Row
            title="Notification sound"
            hint="Play a sound when a new message or update arrives while you're online."
          >
            <Switch checked={form.playSound} onCheckedChange={set("playSound")} />
          </Row>
        </Card>

        <Card Icon={User} title="Account" description="Your sign-in details and profile.">
          <Row
            title="Username"
            hint="Your handle on the marketplace. Freelancers may see this in place of your name."
          >
            {/* :69 "controls cap at 100% width" — the 200px floor is what makes
                this box wider than the 365 card (measured right=387 vs 335). */}
            <div
              className="flex items-center overflow-hidden rounded-[9px] border-[1.5px] border-border bg-secondary focus-within:border-primary focus-within:bg-card max-[641px]:w-full"
              data-testid="username-box"
            >
              <span className="py-[9px] pr-1 pl-3 text-[13.5px] font-semibold text-ink-3">@</span>
              <Input
                type="text"
                value={form.username}
                onChange={(e) => set("username")(e.target.value)}
                aria-label="Username"
                className="min-w-[200px] border-none bg-transparent py-[9px] pr-3 pl-0 text-[13.5px] font-medium text-foreground outline-none h-auto shadow-none dark:bg-transparent md:text-[13.5px] max-[641px]:min-w-0 max-[641px]:flex-1 focus-visible:ring-0"
              />
            </div>
          </Row>
          <Row title="Email address" hint="Used for sign-in and all account notifications.">
            <span className="text-[13.5px] font-medium text-muted-foreground">
              {account.email}
            </span>
          </Row>
          <Row title="Time zone" hint="Deadlines and activity times are shown in this zone.">
            <Combobox
              options={timezoneOptions}
              value={form.timezone}
              onValueChange={set("timezone")}
              aria-label="Time zone"
              searchPlaceholder="Search time zones..."
              // The trigger is already `w-full`; the 260px floor is the actual
              // overflow (measured right=477 against a 390 card edge at 420).
              className="min-w-[260px] max-[641px]:min-w-0"
              data-testid="timezone-combobox"
            />
          </Row>
          <Row title="Password" hint="Change the password you use to sign in.">
            <Button variant="outline" size="sm" asChild>
              <a href={account.passwordPath}>Change password</a>
            </Button>
          </Row>
          {/* AT-01 — in-app Inertia navigation to the Atrium Public Profile
              preview (was a hard <a> into the legacy ERB profile). */}
          <Row title="Public profile" hint="How freelancers see you across the marketplace.">
            <Button variant="outline" size="sm" asChild>
              <Link href={account.profilePath}>
                <Eye className="size-4" strokeWidth={2} />
                View profile
              </Link>
            </Button>
          </Row>
        </Card>

        <div className="sticky bottom-0 -mx-[30px] flex items-center gap-3.5 border-t border-border bg-background px-[30px] py-4">
          <div className="flex-1" />
          <Button
            variant="ghost"
            onClick={() => setForm(baseline)}
            disabled={!dirty || saving}
          >
            Cancel
          </Button>
          <Button onClick={save} disabled={!dirty || saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </>
  )
}

Settings.layout = (page) => (
  <AppShell active="Settings" crumb={["Account", "Settings"]}>
    {page}
  </AppShell>
)
